import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chiama } from './cliente.js';

/**
 * Password e accessi.
 *
 * Tre giri: cambiarsi la propria password, chiedere il ripristino quando la
 * si è dimenticata, e — per chi amministra — dare o togliere l'accesso a un
 * operatore.
 */

export function useCambiaPassword() {
  return useMutation({
    mutationFn: (dati: { currentPassword: string; newPassword: string }) =>
      chiama<{ changed: true }>('/api/auth/password', { metodo: 'POST', corpo: dati }),
  });
}

export interface EsitoDimenticata {
  sent: true;
  message: string;
  /**
   * Il link, quando la posta non è configurata: serve solo in sviluppo per
   * provare il giro senza una casella vera.
   */
  devLink?: string;
}

export function useDimenticata() {
  return useMutation({
    mutationFn: (email: string) =>
      chiama<EsitoDimenticata>('/api/auth/forgot-password', {
        metodo: 'POST',
        corpo: { email },
      }),
  });
}

export function useRipristina() {
  return useMutation({
    mutationFn: (dati: { token: string; newPassword: string }) =>
      chiama<{ changed: true }>('/api/auth/reset-password', { metodo: 'POST', corpo: dati }),
  });
}

/* ------------------------------------------ l'accesso di un operatore */

export interface Accesso {
  email: string | null;
  emailConfirmed: boolean;
  suspendedAt: string | null;
  lastLoginAt: string | null;
  /** Falso quando l'operatore c'è ma non ha ancora credenziali. */
  hasCredentials: boolean;
  accessLevel: string;
  /** In quali aziende lavora. Gli amministratori entrano in tutte. */
  companyIds: number[];
  companies: { id: number; code: string; name: string }[];
}

export interface DatiAccesso {
  email?: string;
  password?: string;
  suspended?: boolean;
  accessLevel?: string;
  companyIds?: number[];
}

export function useAccessoOperatore(id: number, attivo: boolean) {
  return useQuery({
    queryKey: ['accesso', id],
    queryFn: () => chiama<Accesso>(`/api/identities/${id}/access`),
    enabled: attivo && Number.isInteger(id) && id > 0,
    retry: false,
  });
}

export function useSalvaAccesso(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: DatiAccesso) =>
      chiama<Accesso>(`/api/identities/${id}/access`, { metodo: 'PUT', corpo: dati }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['accesso', id] });
    },
  });
}
